import Vue from "vue";
import { ActionTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import moment from "moment";
import { saveAs } from "file-saver";

interface YearProductReportState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  filter: {
    dZoneGroups: any;
    targetDates: Date[];
  };

  items: any[];
}

const state: YearProductReportState = {
  loading: false,
  loaded: false,
  fatalError: false,

  filter: {
    dZoneGroups: null,
    targetDates: [new Date(), new Date()],
  },

  items: [],
};

function buildFilter(params: any) {
  const queryParams: any = {};

  if (params.product_type_ids && params.product_type_ids.length > 0) {
    queryParams.product_type_ids = Array.isArray(params.product_type_ids)
      ? params.product_type_ids
      : [params.product_type_ids];
  }

  if (params.years && params.years.length > 0) {
    queryParams.years = Array.isArray(params.years)
      ? params.years
      : [params.years];
  }

  if (params.group_by_product_type) {
    queryParams.group_by_product_type = params.group_by_product_type;
  }

  if (
    params.show_transport_costs &&
    (!params.group_by_product_type || params.group_by_product_type === "false")
  ) {
    queryParams.show_transport_costs = params.show_transport_costs;
  }

  return queryParams;
}

export const REPORT_DATA = "REPORT_DATA";

export const actions: ActionTree<YearProductReportState, RootState> = {
  loadData({ commit, state, dispatch }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      if (Object.values(params).length > 0) {
        commit("START_LOADING");

        vm.$db([
          vm
            .$q(REPORT_DATA)
            .raw("reports.year_product")
            .action(buildFilter(params)),
        ]).then((result: ServerResult) => {
          commit(
            "SET_REPORT_DATA",
            result.getOperation(REPORT_DATA).getContent()
          );
          commit("LOADED_SUCCESS");

          resolve();
        });
      } else {
        resolve();
      }
    });
  },
  exportData({ state }) {
    const months: any = moment.months();
    return new Promise((resolve, reject) => {
      const csvContent: any[] = [];
      state.items.forEach((item: any) => {
        const header: any[] = ["header", "Год", item.name, ...months, "ИТОГО"];
        csvContent.push(header);
        item.data.forEach((dataItem: any) => {
          const row: any[] = [
            dataItem.class,
            dataItem.year,
            dataItem.name,
            ...months.map((month: any, idx: any) =>
              dataItem.months[idx] ? parseFloat(dataItem.months[idx]) : "0,0"
            ),
            parseFloat(dataItem.total),
          ];
          csvContent.push(row);
        });
      });

      resolve(csvContent);
    });
  },
};

export const mutations: MutationTree<YearProductReportState> = {
  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.fatalError = true;
  },

  SET_REPORT_DATA(state, data) {
    const transport_costs = data.transport_costs
      ? data.transport_costs.reduce(
          (acc: any, item: any) => {
            acc[`${item.year}-${item.month}`] = item.cost;
            acc.total += parseFloat(item.cost);
            return acc;
          },
          { total: 0 }
        )
      : null;

    const currentYear = moment().year();
    const currentMonth = moment().month(); // 0-11

    /**
     * Проверяет, должен ли месяц учитываться в итого
     * Для текущего года - только завершённые месяцы (до текущего)
     * Для прошлых лет - все месяцы
     */
    const shouldIncludeInTotal = (
      year: number,
      monthIndex: number
    ): boolean => {
      if (year < currentYear) {
        return true; // Прошлые года - все месяцы
      } else if (year === currentYear) {
        return monthIndex < currentMonth; // Текущий год - только завершённые месяцы
      }
      return false; // Будущие года - не учитываем
    };

    state.items = data.documents.reduce((acc: any[], item: any) => {
      const data: any = {};

      item.data.forEach((value: any) => {
        const key: any = value.year;
        const monthIndex = value.month - 1; // month приходит 1-12, конвертируем в 0-11
        const includeInTotal = shouldIncludeInTotal(
          parseInt(value.year),
          monthIndex
        );

        if (!data[`0-weight-${key}`]) {
          data[`0-weight-${key}`] = {
            key: `0-weight-${key}`,
            year: value.year,
            name: "Объем отгрузок, кг",
            class: "row-weight",
            total: 0,
            months: new Array(12).fill(0),
          };
        }
        data[`0-weight-${key}`].months[monthIndex] = value.weight;
        if (includeInTotal) {
          data[`0-weight-${key}`].total += parseFloat(value.weight) || 0;
        }
        if (!data[`1-income-${key}`]) {
          data[`1-income-${key}`] = {
            key: `1-income-${key}`,
            year: value.year,
            name: "Выручка, руб",
            class: "row-income",
            total: 0,
            months: new Array(12).fill(0),
          };
        }
        data[`1-income-${key}`].months[monthIndex] = value.income;
        if (includeInTotal) {
          data[`1-income-${key}`].total += parseFloat(value.income) || 0;
        }
        if (!data[`2-prime_cost-${key}`]) {
          data[`2-prime_cost-${key}`] = {
            key: `2-prime_cost-${key}`,
            year: value.year,
            name: "Себестоимость, руб",
            class: "row-prime_cost",
            total: 0,
            months: new Array(12).fill(0),
          };
        }
        data[`2-prime_cost-${key}`].months[monthIndex] = value.prime_cost;
        if (includeInTotal) {
          data[`2-prime_cost-${key}`].total +=
            parseFloat(value.prime_cost) || 0;
        }
        if (!data[`3-profit-${key}`]) {
          data[`3-profit-${key}`] = {
            key: `3-profit-${key}`,
            year: value.year,
            name: "Маржинальная прибыль, руб",
            class: "row-profit",
            total: 0,
            months: new Array(12).fill(0),
          };
        }
        data[`3-profit-${key}`].months[monthIndex] = value.profit;
        if (includeInTotal) {
          data[`3-profit-${key}`].total += parseFloat(value.profit) || 0;
        }
        if (transport_costs) {
          if (!data[`4-transport-cost-${key}`]) {
            data[`4-transport-cost-${key}`] = {
              key: `4-transport-cost-${key}`,
              year: value.year,
              name: "Транспортные затраты, руб",
              class: "row-transport-cost",
              total: 0,
              months: new Array(12).fill(0),
            };
          }
          data[`4-transport-cost-${key}`].months[monthIndex] =
            transport_costs[`${key}-${value.month}`] || 0;
          if (includeInTotal) {
            data[`4-transport-cost-${key}`].total +=
              parseFloat(transport_costs[`${key}-${value.month}`]) || 0;
          }
          if (!data[`5-transport-cost-${key}`]) {
            data[`5-transport-cost-${key}`] = {
              key: `5-transport-cost-${key}`,
              year: value.year,
              name: "Транспортные затраты на 1 кг, руб",
              class: "row-transport-cost",
              total: 0,
              months: new Array(12).fill(0),
            };
          }
          const transportCost = transport_costs[`${key}-${value.month}`] || 0;
          const weight = parseFloat(value.weight) || 0;
          data[`5-transport-cost-${key}`].months[monthIndex] =
            weight > 0 ? parseFloat(transportCost) / weight : 0;
          data[`5-transport-cost-${key}`].total =
            data[`0-weight-${key}`].total > 0
              ? data[`4-transport-cost-${key}`].total /
                data[`0-weight-${key}`].total
              : 0;
        }
        if (!data[`6-profit_percent-${key}`]) {
          data[`6-profit_percent-${key}`] = {
            key: `6-profit_percent-${key}`,
            year: value.year,
            name: "Сырьевая маржинальность, %",
            class: "row-profit_percent",
            total: 0,
            months: new Array(12).fill(0),
          };
        }
        data[`6-profit_percent-${key}`].months[monthIndex] =
          value.profit_percent;
        data[`6-profit_percent-${key}`].total =
          data[`1-income-${key}`].total > 0
            ? ((data[`1-income-${key}`].total -
                data[`2-prime_cost-${key}`].total) /
                data[`1-income-${key}`].total) *
              100
            : 0;
      });

      acc.push({
        name: item.product_type,
        weight: item.weight,
        income: item.income,
        prime_cost: item.prime_cost,
        profit: item.profit,
        profit_percent: -1,
        data: Object.values(data).sort((a: any, b: any) =>
          a.key.localeCompare(b.key)
        ),
      });

      return acc;
    }, []);
  },
};

const getters = {};

export const STORE_KEY = "$_year_product_report";

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
